// Relationship section for System Architecture - 195 lines ✅
import { GitBranch, ArrowRight } from 'lucide-react'

interface FlowStep {
  step: number
  title: string
  description: string
}

const issueLifecycle: FlowStep[] = [
  { step: 1, title: 'User Conversation', description: 'User describes issue in natural language' },
  { step: 2, title: 'AI Agent Extraction', description: 'NLP extracts structured data (title, module, version)' },
  { step: 3, title: 'Issue Created', description: 'Issue record inserted into database' },
  { step: 4, title: 'KG Node Created', description: 'Trigger creates knowledge graph node' },
  { step: 5, title: 'KB Search', description: 'Semantic + full-text search for solutions' },
  { step: 6, title: 'Ranked Results', description: 'Results scored by similarity, success rate, recency' },
  { step: 7, title: 'User Selects', description: 'User chooses resolution from results' },
  { step: 8, title: 'Issue Linked', description: 'Issue linked to KB entry' },
  { step: 9, title: 'KG Edge Created', description: 'Trigger creates resolved_by relationship' },
  { step: 10, title: 'Issue Resolved', description: 'Issue status updated to resolved' }
]

const kbLifecycle: FlowStep[] = [
  { step: 1, title: 'Entry Created', description: 'User or AI creates knowledge base entry (draft)' },
  { step: 2, title: 'AI Categorization', description: 'AI suggests category, tags, d365_module' },
  { step: 3, title: 'Duplicate Check', description: 'Semantic search for similar entries (>0.85 similarity)' },
  { step: 4, title: 'Expert Validation', description: 'Subject matter expert reviews and validates' },
  { step: 5, title: 'Embedding Generated', description: 'text-embedding-3-small creates vector (1536-dim)' },
  { step: 6, title: 'KG Node Synced', description: 'Trigger creates/updates knowledge graph node' },
  { step: 7, title: 'Search Indexed', description: 'Entry added to full-text and semantic search' }
]

const documentPipeline: FlowStep[] = [
  { step: 1, title: 'User Upload', description: 'Document uploaded (DOCX, PDF, images)' },
  { step: 2, title: 'Storage', description: 'File stored in Supabase Storage bucket' },
  { step: 3, title: 'Text Extraction', description: 'Edge Function extracts text content' },
  { step: 4, title: 'Chunking', description: 'Text split into semantic chunks (512 tokens)' },
  { step: 5, title: 'Embedding', description: 'Each chunk embedded with text-embedding-3-small' },
  { step: 6, title: 'Storage', description: 'Chunks and vectors stored in document_chunks' },
  { step: 7, title: 'Search Ready', description: 'Document indexed for semantic search' }
]

function FlowDiagram({ title, steps, icon: Icon }: { title: string; steps: FlowStep[]; icon: React.ElementType }) {
  return (
    <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
      <div className="mb-4 flex items-center gap-2">
        <Icon className="h-6 w-6 text-kaycoo-teal-dark" />
        <h3 className="text-xl font-semibold text-kaycoo-teal-dark">{title}</h3>
      </div>
      <div className="space-y-3">
        {steps.map((step, index) => (
          <div key={step.step}>
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-kaycoo-teal text-sm font-bold text-white">
                {step.step}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-kaycoo-teal-dark">{step.title}</div>
                <div className="text-sm text-kaycoo-charcoal-2">{step.description}</div>
              </div>
            </div>
            {index < steps.length - 1 && (
              <div className="ml-4 flex h-6 items-center">
                <ArrowRight className="h-4 w-4 text-kaycoo-teal-dark" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function DataFlowOverview() {
  return (
    <div className="rounded-lg border border-kaycoo-teal-dark bg-kaycoo-teal-pale p-6">
      <h3 className="mb-4 text-xl font-semibold text-kaycoo-teal-dark">Component Dependencies</h3>
      <div className="space-y-4">
        <div className="rounded-lg bg-white p-4">
          <div className="mb-2 font-semibold text-kaycoo-teal-dark">Frontend → Backend</div>
          <div className="space-y-1 text-sm text-kaycoo-charcoal-2">
            <div>React Components → Supabase JS Client → PostgreSQL (RLS enforced)</div>
            <div>Database Triggers → Edge Functions (business logic)</div>
            <div>Edge Functions → Kaycoo Agentic Platform</div>
            <div>Kaycoo Platform → AI Providers (OpenAI, etc.)</div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-4">
          <div className="mb-2 font-semibold text-kaycoo-teal-dark">Authentication Flow</div>
          <div className="space-y-1 text-sm text-kaycoo-charcoal-2">
            <div>User Login (Supabase Auth) → JWT Token issued</div>
            <div>app_metadata.organization_id injected into token</div>
            <div>RLS policies enforce tenant isolation</div>
            <div>All queries automatically filtered by organization_id</div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-4">
          <div className="mb-2 font-semibold text-kaycoo-teal-dark">Knowledge Graph Auto-Sync</div>
          <div className="space-y-1 text-sm text-kaycoo-charcoal-2">
            <div>Entity INSERT/UPDATE → sync_to_kg() trigger</div>
            <div>Trigger checks validation status → Creates/updates kg_node</div>
            <div>Relationship established → Creates kg_edge</div>
            <div>Graph updated in real-time via database triggers</div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function RelationshipSection() {
  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
        <h2 className="mb-4 text-2xl font-bold text-kaycoo-teal-dark">Data Flows & Relationships</h2>
        <p className="text-kaycoo-charcoal-2">
          Visual representation of how data flows through the system from user interaction to resolution.
        </p>
      </div>

      <FlowDiagram title="Issue Lifecycle" steps={issueLifecycle} icon={GitBranch} />
      <FlowDiagram title="Knowledge Entry Lifecycle" steps={kbLifecycle} icon={GitBranch} />
      <FlowDiagram title="Document Processing Pipeline" steps={documentPipeline} icon={GitBranch} />
      <DataFlowOverview />
    </div>
  )
}
